import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Button, 
  TextField, 
  Box, 
  Typography, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Alert,
  AlertTitle,
  Collapse,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MedicationIcon from '@mui/icons-material/Medication';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SecurityIcon from '@mui/icons-material/Security';
import { createPrescription, updatePharmacy } from '../../redux/slices/prescriptionsSlice';
import { searchMedications } from '../../redux/slices/medicationsSlice';
import { searchPharmacies } from '../../utils/googlePlaces';
import allergyService from '../../services/allergyService';

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const MedicationCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: '1px solid',
  borderColor: theme.palette.divider,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  marginBottom: theme.spacing(2),
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
}));

const SafetyButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  backgroundColor: '#ff9800',
  color: 'white',
  '&:hover': {
    backgroundColor: '#f57c00',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.grey[300],
  },
}));

const WarningDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
  color: 'white',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const SuccessDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
  color: 'white',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const emptyMedication = {
  name: '',
  dosage: '',
  route: '',
  frequency: '',
  duration: ''
};

const routeOptions = [
  'Oral',
  'Intravenous',
  'Intramuscular',
  'Subcutaneous',
  'Topical',
  'Inhalation'
];

const PrescriptionForm = ({ patientId, facilityId, defaultPharmacy, onClose }) => {
  const dispatch = useDispatch();
  const { searchResults: medicationSearchOptions, loading: isSearching } = useSelector(state => state.medications);
  const [medications, setMedications] = useState([{ ...emptyMedication }]);
  const [pharmacy, setPharmacy] = useState(defaultPharmacy || { name: '', address: '', phone: '' });
  const [showPharmacyDialog, setShowPharmacyDialog] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  
  // Safety check state
  const [safetyCheckLoading, setSafetyCheckLoading] = useState(false);
  const [safetyCheckResult, setSafetyCheckResult] = useState(null);
  const [showSafetyDialog, setShowSafetyDialog] = useState(false);

  const handleAddMedication = () => {
    setMedications([...medications, { ...emptyMedication }]);
  };

  const handleRemoveMedication = (index) => {
    const newMedications = medications.filter((_, i) => i !== index);
    setMedications(newMedications);
  };

  const handleMedicationChange = (index, field, value) => {
    const newMedications = [...medications];
    newMedications[index] = {
      ...newMedications[index],
      [field]: value
    };
    setMedications(newMedications);

    if(field === 'name' && value.length > 2) {
      dispatch(searchMedications(value));
    }
  };

  const handlePharmacyChange = async (e) => {
    const { name, value } = e.target;
    setPharmacy(prev => ({ ...prev, [name]: value }));
    
    if (name === 'name' && value.length >= 3) {
      try {
        const results = await searchPharmacies(value);
        setSearchResults(results || []);
      } catch (error) {
        console.error('Error searching pharmacies:', error);
        setSearchResults([]);
      }
    } else if (name === 'name' && value.length < 3) {
      setSearchResults([]);
    }
  };

  const handleSearchResultClick = (result) => {
    setPharmacy({
      name: result.name,
      address: result.address,
      phone: result.phone || '',
      location: result.location
    });
    setSearchResults([]);
  };

  const handlePharmacyUpdate = () => {
    dispatch(updatePharmacy({ patientId, pharmacyDetails: pharmacy }));
    setShowPharmacyDialog(false);
  };

  // Handle safety check
  const handleSafetyCheck = async () => {
    // Validate we have medications to check
    const medicationsToCheck = medications.filter(med => med.name && med.name.trim());
    if (medicationsToCheck.length === 0) {
      return;
    }

    setSafetyCheckLoading(true);
    try {
      const result = await allergyService.checkPrescriptionSafety(
        patientId,
        medicationsToCheck.map(med => ({
          name: med.name,
          rxNormConceptId: med.rxNormConceptId,
          dosage: med.dosage
        })),
        facilityId
      );
      setSafetyCheckResult(result);
      setShowSafetyDialog(true);
    } catch (error) {
      console.error('Error checking prescription safety:', error);
      // Show error in dialog
      setSafetyCheckResult({
        hasWarnings: false,
        riskLevel: 'none',
        allergyWarnings: [],
        interactionWarnings: [],
        error: 'Unable to perform safety check. Please try again.'
      });
      setShowSafetyDialog(true);
    } finally {
      setSafetyCheckLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await dispatch(createPrescription({
      patientId,
      medications,
      pharmacy
    }));
    onClose();
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Pharmacy Section */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: '12px' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocalPharmacyIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Pharmacy Information
            </Typography>
          </Box>
          {pharmacy.name ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              p: 2, 
              bgcolor: 'success.light', 
              borderRadius: '8px' 
            }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {pharmacy.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pharmacy.address}
                </Typography>
              </Box>
              <ModernButton 
                variant="outlined" 
                size="small"
                onClick={() => setShowPharmacyDialog(true)}
              >
                Change
              </ModernButton>
            </Box>
          ) : (
            <ModernButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowPharmacyDialog(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              Add Pharmacy
            </ModernButton>
          )}
        </CardContent>
      </Card>

      {/* Medications Section */}
      {medications.map((medication, index) => (
        <MedicationCard key={index}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicationIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Medication {index + 1}
                </Typography>
              </Box>
              {medications.length > 1 && (
                <IconButton 
                  onClick={() => handleRemoveMedication(index)} 
                  color="error"
                  size="small"
                  sx={{ 
                    bgcolor: 'error.light', 
                    '&:hover': { bgcolor: 'error.main', color: 'white' } 
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <Autocomplete
                freeSolo
                options={medicationSearchOptions || []}
                loading={isSearching}
                getOptionLabel={(option) => option.label || ''}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === 'input') {
                    handleMedicationChange(index, 'name', newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const newMedications = [...medications];
                    
                    // Use pre-extracted data from the search results
                    newMedications[index] = {
                      ...newMedications[index],
                      name: newValue.baseName || newValue.label,
                      rxNormConceptId: newValue.rxNormConceptId,
                      dosage: newValue.dosage || '',
                      route: newValue.route || '',
                    };
                    setMedications(newMedications);
                  }
                }}
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    required
                    label="Medication Name"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <StyledTextField
                required
                label="Dosage"
                value={medication.dosage}
                onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                fullWidth
              />
              <FormControl fullWidth required>
                <InputLabel>Route</InputLabel>
                <Select
                  value={medication.route}
                  label="Route"
                  onChange={(e) => handleMedicationChange(index, 'route', e.target.value)}
                  sx={{ borderRadius: '10px' }}
                >
                  {routeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <StyledTextField
                required
                label="Frequency"
                value={medication.frequency}
                onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                fullWidth
              />
              <StyledTextField
                required
                label="Duration"
                value={medication.duration}
                onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                fullWidth
              />
            </Box>
          </CardContent>
        </MedicationCard>
      ))}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
        <ModernButton
          startIcon={<AddIcon />}
          onClick={handleAddMedication}
          variant="outlined"
        >
          Add Another Medication
        </ModernButton>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <SafetyButton
            startIcon={safetyCheckLoading ? <CircularProgress size={20} color="inherit" /> : <WarningAmberIcon />}
            onClick={handleSafetyCheck}
            disabled={safetyCheckLoading || !medications.some(med => med.name && med.name.trim())}
          >
            {safetyCheckLoading ? 'Checking...' : 'Check Safety'}
          </SafetyButton>

          <ModernButton 
            type="submit" 
            variant="contained" 
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
              }
            }}
          >
            Submit Prescription
          </ModernButton>
        </Box>
      </Box>

      {/* Pharmacy Dialog */}
      <StyledDialog open={showPharmacyDialog} onClose={() => setShowPharmacyDialog(false)} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalPharmacyIcon />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
              {pharmacy.name ? 'Update Pharmacy' : 'Add Pharmacy'}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setShowPharmacyDialog(false)} 
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <StyledTextField
            autoFocus
            margin="dense"
            name="name"
            label="Pharmacy Name"
            type="text"
            fullWidth
            value={pharmacy.name}
            onChange={handlePharmacyChange}
            helperText={searchResults.length > 0 ? "Select from the list below" : "Start typing to search pharmacies"}
            sx={{ mb: 2 }}
          />
          {searchResults.length > 0 && (
            <List sx={{ bgcolor: 'grey.50', borderRadius: '8px', mb: 2 }}>
              {searchResults.map((result, index) => (
                <ListItem 
                  button 
                  key={index} 
                  onClick={() => handleSearchResultClick(result)}
                  sx={{ 
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'primary.light' }
                  }}
                >
                  <ListItemText 
                    primary={result.name} 
                    secondary={result.address}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
          <StyledTextField
            margin="dense"
            name="address"
            label="Address"
            type="text"
            fullWidth
            value={pharmacy.address}
            onChange={handlePharmacyChange}
            disabled
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <ModernButton onClick={() => setShowPharmacyDialog(false)} variant="outlined">
            Cancel
          </ModernButton>
          <ModernButton 
            onClick={handlePharmacyUpdate} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            {pharmacy.name ? 'Update' : 'Add'} Pharmacy
          </ModernButton>
        </DialogActions>
      </StyledDialog>

      {/* Safety Check Results Dialog */}
      <StyledDialog 
        open={showSafetyDialog} 
        onClose={() => setShowSafetyDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        {safetyCheckResult?.hasWarnings ? (
          <WarningDialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <WarningAmberIcon sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                  Safety Warnings Detected
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                  Risk Level: {safetyCheckResult?.riskLevel?.toUpperCase() || 'UNKNOWN'}
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={() => setShowSafetyDialog(false)} 
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </WarningDialogTitle>
        ) : (
          <SuccessDialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircleIcon sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                  Safety Check Passed
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                  No warnings detected
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={() => setShowSafetyDialog(false)} 
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </SuccessDialogTitle>
        )}
        <DialogContent sx={{ p: 3 }}>
          {/* Error message if check failed */}
          {safetyCheckResult?.error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              <AlertTitle>Error</AlertTitle>
              {safetyCheckResult.error}
            </Alert>
          )}

          {/* No warnings - success message */}
          {!safetyCheckResult?.error && !safetyCheckResult?.hasWarnings && (
            <Alert 
              severity="success" 
              icon={<SecurityIcon />}
              sx={{ mb: 2, borderRadius: '8px' }}
            >
              <AlertTitle>All Clear</AlertTitle>
              No allergy conflicts or drug interactions detected for the prescribed medications.
              The prescription appears safe based on the patient's documented allergies.
            </Alert>
          )}

          {/* Allergy Warnings Section */}
          {safetyCheckResult?.allergyWarnings?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ErrorIcon color="error" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Allergy Warnings ({safetyCheckResult.allergyWarnings.length})
                </Typography>
              </Box>
              {safetyCheckResult.allergyWarnings.map((warning, index) => (
                <Alert 
                  key={index}
                  severity={warning.severity === 'high' ? 'error' : warning.severity === 'medium' ? 'warning' : 'info'}
                  sx={{ mb: 1.5, borderRadius: '8px' }}
                >
                  <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {warning.medication}
                    <Chip 
                      label={warning.severity?.toUpperCase() || 'WARNING'} 
                      size="small"
                      color={warning.severity === 'high' ? 'error' : warning.severity === 'medium' ? 'warning' : 'default'}
                      sx={{ ml: 1, fontWeight: 600 }}
                    />
                  </AlertTitle>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {warning.message}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Patient Allergy: ${warning.patientAllergy}`}
                      size="small"
                      variant="outlined"
                      color="error"
                    />
                    {warning.reaction && (
                      <Chip 
                        label={`Reaction: ${warning.reaction}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {warning.category && (
                      <Chip 
                        label={`Category: ${warning.category}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Alert>
              ))}
            </Box>
          )}

          {/* Drug Interaction Warnings Section */}
          {safetyCheckResult?.interactionWarnings?.length > 0 && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WarningAmberIcon color="warning" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Drug Interaction Warnings ({safetyCheckResult.interactionWarnings.length})
                </Typography>
              </Box>
              {safetyCheckResult.interactionWarnings.map((warning, index) => (
                <Alert 
                  key={index}
                  severity={warning.severity === 'high' ? 'error' : 'warning'}
                  sx={{ mb: 1.5, borderRadius: '8px' }}
                >
                  <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {warning.category} Class Interaction
                    <Chip 
                      label={warning.severity?.toUpperCase() || 'WARNING'} 
                      size="small"
                      color={warning.severity === 'high' ? 'error' : 'warning'}
                      sx={{ ml: 1, fontWeight: 600 }}
                    />
                  </AlertTitle>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {warning.message}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {warning.drugs?.map((drug, drugIdx) => (
                      <Chip 
                        key={drugIdx}
                        label={drug}
                        size="small"
                        variant="outlined"
                        color="warning"
                      />
                    ))}
                  </Box>
                </Alert>
              ))}
            </Box>
          )}

          {/* Checked medications summary */}
          {!safetyCheckResult?.error && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: '8px' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Medications checked:</strong> {medications.filter(m => m.name).map(m => m.name).join(', ')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Checked at: {safetyCheckResult?.checkedAt ? new Date(safetyCheckResult.checkedAt).toLocaleString() : new Date().toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {safetyCheckResult?.hasWarnings ? (
            <>
              <ModernButton 
                onClick={() => setShowSafetyDialog(false)} 
                variant="outlined"
              >
                Modify Prescription
              </ModernButton>
              <ModernButton 
                onClick={() => setShowSafetyDialog(false)}
                variant="contained"
                color="warning"
                sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
              >
                Acknowledge & Continue
              </ModernButton>
            </>
          ) : (
            <ModernButton 
              onClick={() => setShowSafetyDialog(false)}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
              }}
            >
              Continue with Prescription
            </ModernButton>
          )}
        </DialogActions>
      </StyledDialog>
    </Box>
  );
};

export default PrescriptionForm;
